import api from './api';

export function getSessionId() {
  try {
    let sid = sessionStorage.getItem('_sr_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('_sr_sid', sid);
    }
    return sid;
  } catch {
    return null;
  }
}

export function getDevice() {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

/** Logged-in user id from localStorage (guest → null). */
export function getAnalyticsUserId() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?._id || user?.id || null;
  } catch {
    return null;
  }
}

/**
 * Weighted brand/category interest learning (guest + logged-in).
 * weight: skip −1, dwell≥3s +2, caption +2, card_tap +3, wishlist +5, affiliate +6
 */
export function trackInterest(product, weight = 1) {
  if (!product || !weight) return;
  try {
    const stored = localStorage.getItem('stockroom_interests') || '{"categories":{},"brands":{}}';
    const interests = JSON.parse(stored);
    if (!interests.categories) interests.categories = {};
    if (!interests.brands) interests.brands = {};

    const cat = product.category || 'Other';
    const brand = product.brand || 'Unknown';
    const delta = Number(weight) || 0;

    interests.categories[cat] = Math.max(0, (interests.categories[cat] || 0) + delta);
    interests.brands[brand] = Math.max(0, (interests.brands[brand] || 0) + delta);

    localStorage.setItem('stockroom_interests', JSON.stringify(interests));
  } catch (e) {
    console.error('Failed to track interest:', e);
  }
}

/**
 * Meta Pixel Search. Fired when a search is submitted, not on each keystroke.
 */
export function trackMetaSearch(searchString, extra = {}) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  const query = String(searchString || '').trim();
  if (!query) return;
  window.fbq('track', 'Search', {
    search_string: query,
    ...extra,
  });
}

/**
 * Meta Pixel AddToWishlist. Fired only when a product is saved, not when it is removed.
 */
export function trackMetaAddToWishlist(product) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function' || !product) return;
  const id = String(product._id || product.id || product.productId || '');
  if (!id) return;
  const price = Number(String(product.price ?? product.productPrice ?? '').replace(/[^0-9.]/g, ''));
  window.fbq('track', 'AddToWishlist', {
    content_ids: [id],
    content_type: 'product',
    content_name: product.name || product.productName || undefined,
    ...(Number.isFinite(price) && price > 0 ? { value: price, currency: 'AUD' } : {}),
  });
}

/**
 * Meta Pixel CompleteRegistration. Fired once when an account is created.
 */
export function trackMetaCompleteRegistration() {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  window.fbq('track', 'CompleteRegistration', { status: true });
}

/**
 * Meta Pixel InitiateCheckout. Fired when watchlist checkout starts.
 */
export function trackMetaInitiateCheckout(items) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
  const contents = list
    .map((item) => {
      const id = String(item.productId || item._id || item.id || '');
      const price = Number(item.productPrice ?? item.price ?? 0) || 0;
      return { id, quantity: 1, price };
    })
    .filter((item) => item.id);
  if (!contents.length) return;
  const value = contents.reduce((sum, item) => sum + item.price, 0);
  window.fbq('track', 'InitiateCheckout', {
    content_ids: contents.map((item) => item.id),
    content_type: 'product',
    contents: contents.map(({ id, quantity }) => ({ id, quantity })),
    num_items: contents.length,
    ...(value > 0 ? { value, currency: 'AUD' } : {}),
  });
}

/**
 * Meta Pixel ViewContent. Fired when a product is opened from a card
 * or when its page loads. The same product is only sent once per few seconds
 * so a click followed by the product page does not count twice.
 */
const recentViewContent = new Map();

export function trackMetaViewContent(product) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function' || !product) return;
  const id = String(product._id || product.id || product.productId || '');
  if (!id) return;
  const now = Date.now();
  if (now - (recentViewContent.get(id) || 0) < 4000) return;
  recentViewContent.set(id, now);
  const price = Number(product.price);
  window.fbq('track', 'ViewContent', {
    content_ids: [id],
    content_type: 'product',
    content_name: product.name || undefined,
    content_category: product.category || undefined,
    ...(Number.isFinite(price) && price > 0 ? { value: price, currency: 'AUD' } : {}),
  });
}

/**
 * Fire-and-forget mid-funnel event.
 * eventType: 'card_tap' | 'pdp_view'
 */
export function trackProductView({
  productId,
  eventType,
  feedSection = 'unknown',
  position = null,
} = {}) {
  if (!productId || !eventType) return;
  api.post('/api/products/view', {
    productId,
    eventType,
    feedSection,
    position,
    sessionId: getSessionId(),
    device: getDevice(),
    userId: getAnalyticsUserId(),
    referrer: typeof document !== 'undefined' ? (document.referrer || 'direct') : null,
  }).catch(() => {});
}

const feedEventQueue = [];
let feedEventFlushTimer = null;

function flushFeedEvents() {
  if (feedEventQueue.length === 0) return;
  const batch = feedEventQueue.splice(0, feedEventQueue.length);
  api.post('/api/products/feed-event', { events: batch }).catch(() => {});
}

/**
 * Queue Deal Feed interaction events (dwell, skip, caption, wishlist).
 * Batches for efficiency; flushes quickly so scoring still sees them.
 */
export function trackDealFeedEvent({
  productId,
  eventType,
  feedSection = 'deal_scroller',
  position = null,
  dwellMs = null,
} = {}) {
  if (!productId || !eventType) return;
  feedEventQueue.push({
    productId,
    eventType,
    feedSection,
    position,
    dwellMs,
    sessionId: getSessionId(),
    device: getDevice(),
    userId: getAnalyticsUserId(),
  });
  if (!feedEventFlushTimer) {
    feedEventFlushTimer = setTimeout(() => {
      feedEventFlushTimer = null;
      flushFeedEvents();
    }, 400);
  }
}

export function flushDealFeedEventsNow() {
  if (feedEventFlushTimer) {
    clearTimeout(feedEventFlushTimer);
    feedEventFlushTimer = null;
  }
  flushFeedEvents();
}
